import React from 'react';
import { Loader, Pagination, UnstyledButton } from '@mantine/core';
import type { AdminSessionsSortKey } from '../../../services/admin-sessions/admin-sessions.service';
import { SESSION_SORT_LABELS, SESSION_STATE_LABELS } from '../monitoring-page.constants';
import { formatDateTime, formatMoney } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type SessionsTableProps = Pick<
  UseMonitoringPage,
  | 'sessionRows'
  | 'isSessionLoading'
  | 'hasLoadedSessions'
  | 'sessionSortKey'
  | 'sessionSortDirection'
  | 'toggleSessionSort'
  | 'sessionTotalPages'
  | 'sessionPage'
  | 'setSessionPage'
>;

export const SessionsTable: React.FC<SessionsTableProps> = ({
  sessionRows,
  isSessionLoading,
  hasLoadedSessions,
  sessionSortKey,
  sessionSortDirection,
  toggleSessionSort,
  sessionTotalPages,
  sessionPage,
  setSessionPage,
}) => {
  const renderSortableHeader = (key: AdminSessionsSortKey) => {
    const isActive = sessionSortKey === key;
    const indicator = !isActive ? '↕' : sessionSortDirection === 'asc' ? '↑' : '↓';

    return (
      <UnstyledButton
        className={isActive ? `${classes.sortButton} ${classes.sortButtonActive}` : classes.sortButton}
        onClick={() => toggleSessionSort(key)}
        data-testid={`monitoring-sessions-sort-${key}`}
      >
        <span>{SESSION_SORT_LABELS[key]}</span>
        <span className={classes.sortIndicator} aria-hidden="true">
          {indicator}
        </span>
      </UnstyledButton>
    );
  };

  if (isSessionLoading && !hasLoadedSessions) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>{renderSortableHeader('sessionName')}</th>
            <th>{renderSortableHeader('openingUser')}</th>
            <th>{'נסגרה ע״י'}</th>
            <th>{renderSortableHeader('openTime')}</th>
            <th>{renderSortableHeader('closeTime')}</th>
            <th>{'אופן סגירה'}</th>
            <th>{renderSortableHeader('state')}</th>
            <th>{renderSortableHeader('orderCount')}</th>
            <th>{renderSortableHeader('salesTotal')}</th>
            <th>{renderSortableHeader('cashTotal')}</th>
            <th>{renderSortableHeader('creditTotal')}</th>
          </tr>
        </thead>
        <tbody>
          {sessionRows.map((row) => (
            <tr key={row.sessionId} data-testid={`monitoring-sessions-row-${row.sessionId}`}>
              <td data-label={'משמרת'}>{row.sessionName}</td>
              <td data-label={'נפתחה ע״י'}>{row.openingUser ?? '-'}</td>
              <td data-label={'נסגרה ע״י'}>{row.closedByUser ?? '-'}</td>
              <td data-label={'נפתחה'}>{formatDateTime(row.openTime)}</td>
              <td data-label={'נסגרה'}>{formatDateTime(row.closeTime)}</td>
              <td data-label={'אופן סגירה'}>{row.autoClosed == null ? '—' : row.autoClosed ? 'אוטומטי' : 'ידני'}</td>
              <td data-label={'סטטוס'}>{SESSION_STATE_LABELS[row.state]}</td>
              <td data-label={'מספר הזמנות'}>{row.orderCount}</td>
              <td data-label={'סה״כ מכירות'}>{`${formatMoney(row.salesTotal)} ₪`}</td>
              <td data-label={'מזומן'}>{`${formatMoney(row.cashTotal)} ₪`}</td>
              <td data-label={'אשראי'}>{`${formatMoney(row.creditTotal)} ₪`}</td>
            </tr>
          ))}
          {sessionRows.length === 0 && (
            <tr>
              <td colSpan={11} className={classes.empty}>
                {'אין הפעלות המתאימות לסינון הנוכחי.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {sessionTotalPages > 1 && (
        <div className={classes.pagination}>
          <Pagination total={sessionTotalPages} value={sessionPage} onChange={setSessionPage} size="sm" />
        </div>
      )}
    </div>
  );
};
